import React from "react";

import { styled } from "styled-components";

import { Colors, colorCSS, fontSizes, gradientCSS } from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

export const Board: React.FC<Colors & React.HTMLAttributes<HTMLDivElement>> = ({
    background,
    color,
    linkColor,
    linkHoverColor,
    medium,
    headingColor,
    borderColor,
    ...props
}) => {
    const style: React.CSSProperties = {
        ...(background && { background }),
        backgroundSize: "cover",
        backgroundPosition: "center",
        borderRadius: "1rem",
        boxShadow: `0 10px 20px ${colorCSS("N400", 0.29)}`,
        cursor: "default",
        fontSize: fontSizes.sm,
        marginBottom: "30px",
        overflow: "hidden",
        padding: "1rem",
        position: "relative",
        textAlign: "left"
    };
    return <div style={style} {...props} />;
};

export const BoardName: React.FC<
    Colors & React.HTMLAttributes<HTMLDivElement>
> = ({
    background,
    color,
    linkColor,
    linkHoverColor,
    medium,
    headingColor,
    borderColor,
    ...props
}) => {
    const style: React.CSSProperties = {
        marginBottom: "1rem",
        overflow: "auto"
    };
    // Note: Nested selector styles (h3) cannot be applied via inline styles
    return <div style={style} {...props} />;
};

export const Card = styled.div`
    ${redundantCssFromKebab("background-color", `${colorCSS("N200")}`)}
    ${redundantCssFromKebab("border-radius", "4px")}
    ${redundantCssFromKebab("font-size", "0.9rem")}
    ${redundantCssFromKebab("margin-bottom", "8px")}
    ${redundantCssFromKebab("position", "relative")}
    ${redundantCssFromKebab("overflow", "hidden")}
`;

export const CardContent = styled.div`
    ${redundantCssFromKebab("background-color", `${colorCSS("N0")}`)}
    ${redundantCssFromKebab("padding", "6px 8px")}
`;

export const CardImage = styled.img`
    ${redundantCssFromKebab("display", "block")}
    ${redundantCssFromKebab("height", "auto")}
    ${redundantCssFromKebab("max-width", "100%")}
`;

export const Description = styled.div`
    ${redundantCssFromKebab("color", `${colorCSS("N500", 0.3)}`)}
    ${redundantCssFromKebab("margin-bottom", "14px")}

    span {
        ${redundantCssFromKebab("display", "inline-block")}
        ${redundantCssFromKebab("margin-left", "4px")}
        ${redundantCssFromKebab("vertical-align", "middle")}
    }
`;

export const Label = styled.div<{ color?: string }>`
    background-color: ${({ color }: { color?: string }) =>
        colorCSS(
            color === "blue" ? "B200" : color === "purple" ? "P200" : "R200"
        )};
    ${redundantCssFromKebab("border-radius", "3.5px")}
    ${redundantCssFromKebab("height", "7px")}
    ${redundantCssFromKebab("margin-right", "5px")}
    ${redundantCssFromKebab("width", "30px")}
`;

export const Labels = styled.div`
    ${redundantCssFromKebab("margin", "4px 0")}
`;

export const List = styled.div`
    ${redundantCssFromKebab("float", "left")}
    ${redundantCssFromKebab("margin-right", "10px")}
    ${redundantCssFromKebab("width", "225.25px")}
`;

export const ListContent = styled.div`
    ${redundantCssFromKebab("background-color", `${colorCSS("N30")}`)}
    ${redundantCssFromKebab("border-radius", "6px")}
    ${redundantCssFromKebab(
        "box-shadow",
        `0 2px 4px, ${colorCSS("N900", 0.5)}`
    )}
    ${redundantCssFromKebab("padding", "8px")}
`;

export const ListName = styled.h5`
    ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
    ${redundantCssFromKebab("letter-spacing", "0.5px")}
    ${redundantCssFromKebab("margin", "0 0 8px 3px")}
    ${redundantCssFromKebab("text-transform", "none")}
`;

export const Lists = styled.div`
    ${redundantCssFromKebab("min-width", "1000px")}
    ${redundantCssFromKebab("overflow", "auto")}
`;

export const MobileBoard: React.FC<
    Colors & React.HTMLAttributes<HTMLDivElement>
> = ({
    background,
    color,
    linkColor,
    linkHoverColor,
    medium,
    headingColor,
    borderColor,
    ...props
}) => {
    const style: React.CSSProperties = {
        ...(background && { background }),
        borderRadius: "6px",
        boxShadow: "none",
        margin: 0,
        overflow: "hidden",
        padding: "10px",
        textAlign: "left"
    };
    return <div style={style} {...props} />;
};

export const MobileButtons = styled.div`
    div {
        ${redundantCssFromKebab("background-color", `${colorCSS("B400", 0.2)}`)}
        ${redundantCssFromKebab("border-radius", "4px 0 0 4px")}
        ${redundantCssFromKebab("position", "absolute")}
        ${redundantCssFromKebab("width", "6px")}

        &.silent {
            ${redundantCssFromKebab("height", "31px")}
            ${redundantCssFromKebab("left", "-6px")}
            ${redundantCssFromKebab("top", "70px")}
        }

        &.volume {
            ${redundantCssFromKebab("height", "81px")}
            ${redundantCssFromKebab("left", "-6px")}
            ${redundantCssFromKebab("top", "130px")}
        }

        &.lock {
            ${redundantCssFromKebab("border-radius", "0 4px 4px 0")}
            ${redundantCssFromKebab("height", "61px")}
            ${redundantCssFromKebab("right", "-6px")}
            ${redundantCssFromKebab("top", "130px")}
        }
    }
`;

export const MobileClippingMask = styled.div`
    ${redundantCssFromKebab("height", "377px")}
    ${redundantCssFromKebab("overflow", "hidden")}
    ${redundantCssFromKebab("padding", "0 10px")}
`;

export const MobileForehead = styled.div`
    ${redundantCssFromKebab("height", "50px")}
    ${redundantCssFromKebab("opacity", "0.5")}
    ${redundantCssFromKebab("position", "relative")}

    div {
        ${redundantCssFromKebab("height", "8px")}
        ${redundantCssFromKebab("border-radius", "4px")}
        ${redundantCssFromKebab("width", "72px")}
        ${redundantCssFromKebab("margin", "-4px 0 0 -36px")}
        ${redundantCssFromKebab("top", "50%")}
        ${redundantCssFromKebab("left", "50%")}
        ${redundantCssFromKebab("background-color", `${colorCSS("B400", 0.5)}`)}
        ${redundantCssFromKebab("position", "absolute")}

        &:before {
            ${redundantCssFromKebab(
                "background-color",
                `${colorCSS("B400", 0.5)}`
            )}
            ${redundantCssFromKebab("border-radius", "50%")}
            ${redundantCssFromKebab("content", '""')}
            ${redundantCssFromKebab("height", "10px")}
            ${redundantCssFromKebab("position", "absolute")}
            ${redundantCssFromKebab("width", "10px")}
            ${redundantCssFromKebab("margin", "-1px 0 0 -62px")}
        }

        &:after {
            ${redundantCssFromKebab(
                "background-color",
                `${colorCSS("B400", 0.5)}`
            )}
            ${redundantCssFromKebab("border-radius", "50%")}
            ${redundantCssFromKebab("content", '""')}
            ${redundantCssFromKebab("height", "16px")}
            ${redundantCssFromKebab("position", "absolute")}
            ${redundantCssFromKebab("width", "16px")}
            ${redundantCssFromKebab("margin", "-4px 0 0 -91px")}
        }
    }
`;

export const MobileHeader: React.FC<
    Colors & React.HTMLAttributes<HTMLDivElement>
> = ({
    background,
    color,
    linkColor,
    linkHoverColor,
    medium,
    headingColor,
    borderColor,
    ...props
}) => {
    const style: React.CSSProperties = {
        margin: "-10px -10px 10px",
        ...(color && { color }),
        padding: "10px 0",
        position: "relative"
    };
    // Note: Nested selectors (h3, ::before, ::after) cannot be applied via inline styles
    return <div style={style} {...props} />;
};

export const MobilePhone = styled.div`
    ${redundantCssFromKebab("border-radius", "32px")}
    ${redundantCssFromKebab("background", `${gradientCSS("blue-fade")}`)}
    ${redundantCssFromKebab(
        "box-shadow",
        `0 10px 20px ${colorCSS("N400", 0.29)}`
    )}
    ${redundantCssFromKebab("margin", "0 auto")}
    ${redundantCssFromKebab("position", "relative")}
    ${redundantCssFromKebab("width", "290px")}
    ${redundantCssFromKebab("max-width", "100%")}
`;

export const MobileScreen = styled.div`
    ${redundantCssFromKebab("margin", "0 10px")}
    ${redundantCssFromKebab("overflow", "hidden")}

    ${List} {
        ${redundantCssFromKebab("width", "185px")}
    }
`;

export const User = styled.img`
    ${redundantCssFromKebab("height", "28px")}
    ${redundantCssFromKebab("float", "left")}
    ${redundantCssFromKebab("margin-left", "5px")}
    ${redundantCssFromKebab("width", "28px")}
`;

export const Users = styled.div`
    ${redundantCssFromKebab("bottom", "7px")}
    ${redundantCssFromKebab("position", "absolute")}
    ${redundantCssFromKebab("right", "7px")}
`;
